import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import clsx from 'clsx'

interface Props {
  title: string
  /** Optional sub-line. Small grey text under the title. */
  subtitle?: string
  /** When set, renders a back-arrow chip on the left that navigates here.
   *  Pass the magic string `'-1'` for browser-history back. Pass `null`
   *  (the default) to omit the back affordance entirely. */
  backTo?: string | null
  /** Optional right-aligned slot — e.g. a sign pill or an edit chip. */
  right?: React.ReactNode
  className?: string
}

/**
 * Unified page header. Title sits centred on a single row alongside the
 * back chip and (optional) right slot, so vertical alignment never looks
 * off. Subtitle, when present, drops to its own line below the row.
 */
export function PageHeader({ title, subtitle, backTo = null, right, className }: Props) {
  const navigate = useNavigate()
  return (
    <header className={clsx('flex flex-col gap-1', className)}>
      <div className="flex items-center gap-2">
        {backTo !== null && (
          <button
            onClick={() => (backTo === '-1' ? navigate(-1) : navigate(backTo))}
            aria-label="Назад"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sage-100/70 text-sage-700 transition hover:bg-sage-200/80 active:scale-[0.95]"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.8} aria-hidden />
          </button>
        )}
        <h1 className="min-w-0 flex-1 truncate font-serif text-2xl leading-tight text-sage-800">
          {title}
        </h1>
        {right && <div className="shrink-0">{right}</div>}
      </div>
      {subtitle && (
        <p className={clsx('text-sm text-sage-600', backTo !== null && 'pl-10')}>
          {subtitle}
        </p>
      )}
    </header>
  )
}
